import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { LANGS, alternatePath, resolveRoute, buildPath } from "@/i18n/routes";

const SITE_URL = (process.env.REACT_APP_SITE_URL || "").replace(/\/$/, "") || (typeof window !== "undefined" && window.location.origin) || "";
export { SITE_URL };
const BRAND = "Hotel Ciutat Martorell";
const DEFAULT_TITLE = "Hotel Ciutat Martorell 4★ · Martorell, Barcelona";
const DEFAULT_OG = "/images/og/og-default.jpg";
const PHONE = "+34937745160";
const EMAIL = "info@hotel-martorell.com";
const SAME_AS = [
  "https://www.biospheresustainable.com/en/community/hotel-ciutat-martorell/8387",
  "https://www.instagram.com/hotel_ciutat_martorell/",
  "https://www.facebook.com/hotelciutatmartorell",
];

const OG_LOCALE = { es: "es_ES", en: "en_GB", ca: "ca_ES", fr: "fr_FR", de: "de_DE", it: "it_IT", zh: "zh_CN", ja: "ja_JP" };
const LANG_NAMES = { es: "Spanish", en: "English", ca: "Catalan", fr: "French", de: "German", it: "Italian", zh: "Chinese", ja: "Japanese" };

/** Parent route of each nested route (for hierarchical breadcrumbs). */
const PARENTS = {
  roomDetail: "rooms", meetingRooms: "corporate", meetingRoomDetail: "meetingRooms",
  weddings: "events", celebrations: "events", groups: "events", blogPost: "blog", survey: "biosphere",
};

function upsertMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href, extra = {}) {
  const selector = `link[rel="${rel}"]${extra.hreflang ? `[hreflang="${extra.hreflang}"]` : ""}`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function setLocaleAlternates(current) {
  document.head.querySelectorAll('meta[property="og:locale:alternate"]').forEach((el) => el.remove());
  LANGS.filter((l) => l !== current).forEach((l) => {
    const el = document.createElement("meta");
    el.setAttribute("property", "og:locale:alternate");
    el.setAttribute("content", OG_LOCALE[l] || l);
    document.head.appendChild(el);
  });
}

/** Brand suffix only when it fits in a SERP title (~60-65 chars) and isn't already present. */
function buildTitle(title) {
  if (!title) return DEFAULT_TITLE;
  if (title.includes("Ciutat Martorell")) return title;
  const full = `${title} | ${BRAND}`;
  return full.length <= 65 ? full : title;
}

/** Keep meta descriptions within ~160 chars, cutting at a sentence or word boundary. */
function trimDescription(desc) {
  if (!desc) return "";
  if (desc.length <= 160) return desc;
  const cut = desc.slice(0, 157);
  const i = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf(", "), cut.lastIndexOf(" "));
  const base = (i > 80 ? cut.slice(0, i) : cut).replace(/[,;:\s]+$/, "");
  return base.endsWith(".") ? base : `${base}…`;
}

const amenity = (name) => ({ "@type": "LocationFeatureSpecification", name, value: true });

export const HOTEL_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Hotel",
  "@id": SITE_URL + "/#hotel",
  name: BRAND,
  alternateName: "Hotel Ciutat Martorell 4*",
  starRating: { "@type": "Rating", ratingValue: "4" },
  telephone: PHONE,
  email: EMAIL,
  address: {
    "@type": "PostalAddress",
    streetAddress: "Av. Pau Claris s/n",
    postalCode: "08760",
    addressLocality: "Martorell",
    addressRegion: "Barcelona",
    addressCountry: "ES",
  },
  geo: { "@type": "GeoCoordinates", latitude: 41.4787504, longitude: 1.9212783 },
  checkinTime: "15:00",
  checkoutTime: "12:00",
  numberOfRooms: 92,
  smokingAllowed: false,
  currenciesAccepted: "EUR",
  paymentAccepted: "Cash, Credit Card",
  priceRange: "€€",
  amenityFeature: [
    "Free fibre WiFi", "Private parking", "Restaurant My Bar", "Meeting rooms", "Banquet hall", "Fitness room",
    "Business Corner", "24-hour reception", "24-hour room service", "Air conditioning", "Lift", "Luggage storage",
    "Laundry service", "Accessible rooms", "Breakfast buffet",
  ].map(amenity),
  containsPlace: [
    { "@type": "HotelRoom", name: "Habitación Doble · Double Room", bed: { "@type": "BedDetails", typeOfBed: "Double", numberOfBeds: 1 },
      occupancy: { "@type": "QuantitativeValue", maxValue: 2 }, floorSize: { "@type": "QuantitativeValue", value: 21, unitCode: "MTK" } },
    { "@type": "HotelRoom", name: "Habitación Doble Twin · Twin Room", bed: { "@type": "BedDetails", typeOfBed: "Single", numberOfBeds: 2 },
      occupancy: { "@type": "QuantitativeValue", maxValue: 2 }, floorSize: { "@type": "QuantitativeValue", value: 21, unitCode: "MTK" } },
  ],
  potentialAction: { "@type": "ReserveAction", target: "https://direct-book.com/properties/bcn1333" },
  url: SITE_URL,
  image: [SITE_URL + DEFAULT_OG, SITE_URL + "/images/exterior/fachada.webp", SITE_URL + "/images/restaurante/restaurante_hero.webp"],
  logo: SITE_URL + "/images/logo.png",
  sameAs: SAME_AS,
  hasMap: "https://maps.app.goo.gl/rJHKbwzQCdEZanGH6",
  parentOrganization: { "@id": SITE_URL + "/#organization" },
};

export const SITE_JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": SITE_URL + "/#organization",
      name: BRAND,
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: SITE_URL + "/images/logo.png" },
      telephone: PHONE,
      email: EMAIL,
      sameAs: SAME_AS,
      contactPoint: [{
        "@type": "ContactPoint", telephone: PHONE, email: EMAIL, contactType: "reservations", areaServed: "ES",
        availableLanguage: LANGS.map((l) => LANG_NAMES[l]),
      }],
    },
    {
      "@type": "WebSite",
      "@id": SITE_URL + "/#website",
      url: SITE_URL,
      name: BRAND,
      publisher: { "@id": SITE_URL + "/#organization" },
      inLanguage: LANGS,
    },
  ],
};

/** FAQPage JSON-LD from CMS faqs (returns null when empty). */
export function faqJsonLd(faqs, L) {
  if (!faqs?.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: L(f.q), acceptedAnswer: { "@type": "Answer", text: L(f.a) } })),
  };
}

function breadcrumbLabel(key, t) {
  const map = {
    rooms: t.nav.rooms, corporate: t.nav.corporate, meetingRooms: t.corporate.salasEyebrow,
    events: t.nav.events, blog: t.nav.blog, biosphere: t.nav.biosphere,
  };
  return map[key] || key;
}

export function Seo({ title, description, image, jsonLd, type = "website", noindex = false, home = false }) {
  const { pathname } = useLocation();
  const { lang, t } = useLang();

  useEffect(() => {
    const fullTitle = buildTitle(title);
    const desc = trimDescription(description);
    document.title = fullTitle;
    upsertMeta("name", "description", desc);
    upsertMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large");
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", SITE_URL + pathname);
    upsertMeta("property", "og:locale", OG_LOCALE[lang] || "es_ES");
    setLocaleAlternates(lang);
    upsertMeta("property", "og:site_name", BRAND);
    const ogImage = image ? (image.startsWith("http") ? image : SITE_URL + image) : SITE_URL + DEFAULT_OG;
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", desc);
    upsertMeta("name", "twitter:image", ogImage);

    const esPath = alternatePath(pathname, "es");
    upsertLink("canonical", SITE_URL + pathname);
    LANGS.forEach((l) => {
      const p = l === lang ? pathname : alternatePath(pathname, l);
      upsertLink("alternate", SITE_URL + p, { hreflang: l });
    });
    upsertLink("alternate", SITE_URL + esPath, { hreflang: "x-default" });

    setJsonLd("jsonld-site", SITE_JSONLD);
    setJsonLd("jsonld-hotel", HOTEL_JSONLD);
    setJsonLd("jsonld-page", jsonLd || null);

    // Hierarchical breadcrumb: Home > parent(s) > current. Skipped on home and noindex pages.
    let breadcrumb = null;
    if (title && !noindex && !home) {
      const homeUrl = SITE_URL + (lang === "es" ? "/" : "/" + lang);
      const chain = [];
      let key = resolveRoute(pathname)?.key;
      while (key && PARENTS[key]) {
        key = PARENTS[key];
        chain.unshift({ name: breadcrumbLabel(key, t), item: SITE_URL + buildPath(key, lang) });
      }
      const items = [{ name: BRAND, item: homeUrl }, ...chain, { name: title.split(" · ")[0], item: SITE_URL + pathname }];
      breadcrumb = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: it.item })),
      };
    }
    setJsonLd("jsonld-breadcrumb", breadcrumb);
  }, [title, description, image, jsonLd, type, noindex, home, pathname, lang, t]);

  return null;
}
